import { useState, useCallback, useEffect } from "react";
import { type User } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
// @ts-ignore — AuthContext es JSX, no TSX
import { useAuth } from "@/contexts/AuthContext";
import type { StudentProfile } from "../lib/taskEngine";
import { clearBordonaSessionCache } from "../lib/clearBordonaSessionCache";
import { useBordonaAccount } from "./useBordonaAccount";

const STORAGE_KEY = "bordona-student-profile";
const FIRESTORE_PROFILE_COLLECTION = "bordona_student_profiles";

function loadStoredProfile(): StudentProfile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredProfile(profile: StudentProfile) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

async function loadFirestoreStudentProfile(uid: string): Promise<StudentProfile | null> {
  try {
    const snap = await getDoc(doc(db, FIRESTORE_PROFILE_COLLECTION, uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      uid,
      firstName: data.firstName || "",
      lastName: data.lastName || "",
      rut: data.rut,
      accountType: "estudiante",
      createdAt: typeof data.createdAt === "number" ? data.createdAt : Date.now(),
    };
  } catch {
    return null;
  }
}

export function useStudentAuth() {
  const authCtx: any = useAuth();
  const { isTeacher: isTeacherAccount } = useBordonaAccount();
  const [profile, setProfile] = useState<StudentProfile | null>(null);

  useEffect(() => {
    const user = authCtx?.user;
    if (!user) {
      setProfile(null);
      return;
    }

    if (isTeacherAccount) {
      localStorage.removeItem(STORAGE_KEY);
      setProfile(null);
      return;
    }

    let cancelled = false;

    (async () => {
      const fromFs = await loadFirestoreStudentProfile(user.uid);
      if (cancelled) return;

      if (fromFs) {
        saveStoredProfile(fromFs);
        setProfile(fromFs);
        return;
      }

      const stored = loadStoredProfile();
      if (stored && stored.uid === user.uid) {
        setProfile(stored);
      } else {
        setProfile(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authCtx?.user, isTeacherAccount]);

  const signInAsStudent = useCallback(async (): Promise<boolean> => {
    const returnTo = encodeURIComponent(window.location.href);
    window.location.href = `https://edusico.cl/login?returnTo=${returnTo}`;
    return false;
  }, []);

  const saveProfile = useCallback(
    async (firstName: string, lastName: string, rut?: string): Promise<boolean> => {
      const user: User | null = authCtx?.user || null;
      if (!user) return false;

      const trimmedRut = rut?.trim();
      const newProfile: StudentProfile = {
        uid: user.uid,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        ...(trimmedRut ? { rut: trimmedRut } : {}),
        accountType: "estudiante",
        createdAt: Date.now(),
      };

      const firestorePayload: Record<string, unknown> = {
        uid: newProfile.uid,
        firstName: newProfile.firstName,
        lastName: newProfile.lastName,
        accountType: newProfile.accountType,
        createdAt: newProfile.createdAt,
        email: user.email,
        displayName: `${newProfile.firstName} ${newProfile.lastName}`,
        updatedAt: Date.now(),
      };
      if (trimmedRut) {
        firestorePayload.rut = trimmedRut;
      }

      try {
        await setDoc(
          doc(db, FIRESTORE_PROFILE_COLLECTION, user.uid),
          firestorePayload,
          { merge: true }
        );
        saveStoredProfile(newProfile);
        setProfile(newProfile);
        return true;
      } catch (err) {
        console.error("Error guardando perfil de estudiante Bordona:", err);
        return false;
      }
    },
    [authCtx?.user]
  );

  const logout = useCallback(async () => {
    await authCtx?.logout?.();
    clearBordonaSessionCache();
    setProfile(null);
  }, [authCtx]);

  return {
    user: (authCtx?.user as User) || null,
    isLoading: authCtx?.loading || false,
    isAuthenticated: !!authCtx?.user,
    profile,
    accessProfile: authCtx?.accessProfile || null,
    error: authCtx?.error || null,
    signInAsStudent,
    saveProfile,
    logout,
  };
}
